'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApplicationRoleDto } from '@terra/shared-contracts-applications';
import { CUSTOM_ROLE_CREATE_DRAFT_ID } from '../../../utils/chatEditableObjectUtils';
import {
  useRoleEditStore,
  type CustomRoleEditableField,
} from '../../../store/roleEditStore';
import { useCustomRoleUpdateQueueStore } from '../../../store/customRoleUpdateQueueStore';
import { useCredentialUpdateQueueStore } from '../../../store/credentialUpdateQueueStore';

function buildApplicationRoleStub({
  id,
  applicationId,
  name,
  description,
}: {
  id: string;
  applicationId: string;
  name: string;
  description: string | null;
}): ApplicationRoleDto {
  return {
    id,
    tenantId: null,
    applicationId,
    name,
    description,
    aiGeneratedDescription: null,
    analysisStatus: null,
    analysisJobId: null,
    analysisError: null,
    lastAnalyzedAt: null,
    createdAt: new Date(0),
    updatedAt: new Date(0),
  };
}

export function useRoleCopilotEditor({
  applicationId,
  roles,
}: {
  applicationId: string;
  roles: ApplicationRoleDto[];
}) {
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formDraft, setFormDraft] = useState({ name: '', description: '' });
  const pendingAiEditsByRoleId = useRoleEditStore(
    (state) => state.pendingAiEdits,
  );
  const acceptAiEdits = useRoleEditStore((state) => state.acceptAiEdits);
  const discardAiEdits = useRoleEditStore((state) => state.discardAiEdits);
  const clearRoleEditState = useRoleEditStore(
    (state) => state.clearRoleEditState,
  );
  const completeActiveCustomRoleUpdate = useCustomRoleUpdateQueueStore(
    (state) => state.completeActiveCustomRoleUpdate,
  );
  const activeCustomRoleUpdateId = useCustomRoleUpdateQueueStore(
    (state) => state.activeUpdate?.id ?? null,
  );
  const activeCustomRoleUpdate = useCustomRoleUpdateQueueStore(
    (state) => state.activeUpdate,
  );
  const activeEditorRoleId = isCreating
    ? CUSTOM_ROLE_CREATE_DRAFT_ID
    : editingRoleId;
  const activeRolePendingSignature = useMemo(() => {
    if (!activeEditorRoleId) {
      return '';
    }

    const pending = pendingAiEditsByRoleId[activeEditorRoleId];

    if (!pending) {
      return '';
    }

    return pending.fields
      .map((entry) => `${entry.field}:${entry.before}:${entry.after}`)
      .join('|');
  }, [activeEditorRoleId, pendingAiEditsByRoleId]);
  const resolveActiveCustomRoleEdit = useCallback(() => {
    if (!activeEditorRoleId) {
      return;
    }

    completeActiveCustomRoleUpdate({
      roleId: activeEditorRoleId,
      updateId: activeCustomRoleUpdateId ?? undefined,
      preserveRoleEditState: false,
    });
    const customRoleQueueState = useCustomRoleUpdateQueueStore.getState();
    const nextActiveCustomRoleUpdate = customRoleQueueState.activeUpdate;

    if (customRoleQueueState.hasActiveCustomRoleQueueWork()) {
      if (
        nextActiveCustomRoleUpdate?.objectId === CUSTOM_ROLE_CREATE_DRAFT_ID
      ) {
        setEditingRoleId(null);
        setIsCreating(true);
      } else if (nextActiveCustomRoleUpdate) {
        setIsCreating(false);
        setEditingRoleId(nextActiveCustomRoleUpdate.objectId);
      }
    } else {
      if (activeEditorRoleId === CUSTOM_ROLE_CREATE_DRAFT_ID) {
        setIsCreating(false);
      } else {
        setEditingRoleId(null);
      }
    }

    if (!customRoleQueueState.hasActiveCustomRoleQueueWork()) {
      useCredentialUpdateQueueStore.getState().tryActivateCredentialUpdates();
    }
  }, [
    activeEditorRoleId,
    activeCustomRoleUpdateId,
    completeActiveCustomRoleUpdate,
  ]);
  const editingRole = editingRoleId
    ? roles.find((role) => role.id === editingRoleId)
    : null;
  const syntheticEditingRole = useMemo((): ApplicationRoleDto | null => {
    if (
      editingRole ||
      !editingRoleId ||
      editingRoleId === CUSTOM_ROLE_CREATE_DRAFT_ID
    ) {
      return null;
    }

    const pending = pendingAiEditsByRoleId[editingRoleId];
    const draft = useRoleEditStore.getState().getDraftFields(editingRoleId);
    const pendingName = pending?.fields.find(
      (entry) => entry.field === 'name',
    )?.after;
    const pendingDescription = pending?.fields.find(
      (entry) => entry.field === 'description',
    )?.after;
    const resolvedName = draft?.name || pendingName || formDraft.name;

    if (!resolvedName) {
      return null;
    }

    return buildApplicationRoleStub({
      id: editingRoleId,
      applicationId,
      name: resolvedName,
      description:
        draft?.description ||
        pendingDescription ||
        formDraft.description ||
        null,
    });
  }, [
    applicationId,
    editingRole,
    editingRoleId,
    formDraft.description,
    formDraft.name,
    pendingAiEditsByRoleId,
  ]);
  const resolvedEditingRole = editingRole ?? syntheticEditingRole;

  useEffect(() => {
    if (!activeCustomRoleUpdate) {
      return;
    }

    if (activeCustomRoleUpdate.objectId === CUSTOM_ROLE_CREATE_DRAFT_ID) {
      setEditingRoleId(null);
      setIsCreating(true);

      return;
    }

    setIsCreating(false);
    setEditingRoleId(activeCustomRoleUpdate.objectId);
  }, [activeCustomRoleUpdate]);
  useEffect(() => {
    const pendingRoleIds = Object.keys(pendingAiEditsByRoleId).filter(
      (roleId) => (pendingAiEditsByRoleId[roleId]?.fields.length ?? 0) > 0,
    );

    if (
      pendingRoleIds.length === 0 ||
      editingRoleId ||
      isCreating ||
      activeCustomRoleUpdate
    ) {
      return;
    }

    const firstPendingRoleId = pendingRoleIds.find(
      (roleId) => roleId !== CUSTOM_ROLE_CREATE_DRAFT_ID,
    );

    if (firstPendingRoleId) {
      setEditingRoleId(firstPendingRoleId);
    } else if (pendingAiEditsByRoleId[CUSTOM_ROLE_CREATE_DRAFT_ID]) {
      setIsCreating(true);
    }
  }, [
    activeCustomRoleUpdate,
    editingRoleId,
    isCreating,
    pendingAiEditsByRoleId,
  ]);
  useEffect(() => {
    if (!activeEditorRoleId) {
      return;
    }

    const readPendingFieldAfter = (
      field: CustomRoleEditableField,
    ): string | undefined => {
      const pending = useRoleEditStore
        .getState()
        .getPendingAiEdits(activeEditorRoleId);

      return pending?.fields.find((entry) => entry.field === field)?.after;
    };

    if (activeEditorRoleId === CUSTOM_ROLE_CREATE_DRAFT_ID) {
      const draft = useRoleEditStore
        .getState()
        .getDraftFields(activeEditorRoleId);
      const nextName = draft?.name || readPendingFieldAfter('name') || '';
      const nextDescription =
        draft?.description || readPendingFieldAfter('description') || '';

      setFormDraft((previous) =>
        previous.name === nextName && previous.description === nextDescription
          ? previous
          : { name: nextName, description: nextDescription },
      );

      return;
    }

    const roleFromList = roles.find((entry) => entry.id === activeEditorRoleId);
    const draft = useRoleEditStore
      .getState()
      .getDraftFields(activeEditorRoleId);
    const pending = useRoleEditStore
      .getState()
      .getPendingAiEdits(activeEditorRoleId);
    const pendingName = readPendingFieldAfter('name');
    const pendingDescription = readPendingFieldAfter('description');
    const serverBaseFields = {
      name: roleFromList?.name ?? '',
      description: roleFromList?.description ?? '',
    };

    if ((pending?.fields.length ?? 0) > 0 && serverBaseFields.name) {
      useRoleEditStore
        .getState()
        .syncRoleEditBaseFields(activeEditorRoleId, serverBaseFields);
    }

    const acceptedDraft = useRoleEditStore
      .getState()
      .getDraftFields(activeEditorRoleId);
    const nextName =
      acceptedDraft?.name ||
      draft?.name ||
      pendingName ||
      serverBaseFields.name;
    const nextDescription =
      acceptedDraft?.description ??
      draft?.description ??
      pendingDescription ??
      serverBaseFields.description;

    setFormDraft((previous) =>
      previous.name === nextName && previous.description === nextDescription
        ? previous
        : { name: nextName, description: nextDescription },
    );
  }, [
    activeEditorRoleId,
    roles,
    activeCustomRoleUpdateId,
    activeRolePendingSignature,
  ]);
  const roleForForm = useMemo(() => {
    if (!resolvedEditingRole) {
      return undefined;
    }

    return {
      ...resolvedEditingRole,
      name: formDraft.name || resolvedEditingRole.name,
      description:
        formDraft.description || resolvedEditingRole.description || null,
    };
  }, [formDraft.description, formDraft.name, resolvedEditingRole]);
  const createRoleForForm = useMemo((): ApplicationRoleDto | undefined => {
    if (!formDraft.name && !formDraft.description) {
      return undefined;
    }

    return buildApplicationRoleStub({
      id: CUSTOM_ROLE_CREATE_DRAFT_ID,
      applicationId,
      name: formDraft.name,
      description: formDraft.description || null,
    });
  }, [applicationId, formDraft.description, formDraft.name]);
  const handleAcceptField = useCallback(
    (field: CustomRoleEditableField) => {
      if (!activeEditorRoleId) {
        return;
      }

      const nextDraft = acceptAiEdits(activeEditorRoleId, [field], {
        baseDraftFields: formDraft,
      });

      if (nextDraft) {
        setFormDraft(nextDraft);
      }
    },
    [acceptAiEdits, activeEditorRoleId, formDraft],
  );
  const handleDiscardField = useCallback(
    (field: CustomRoleEditableField) => {
      if (!activeEditorRoleId) {
        return;
      }

      discardAiEdits(activeEditorRoleId, [field]);
    },
    [activeEditorRoleId, discardAiEdits],
  );
  const handleAcceptAll = useCallback(() => {
    if (!activeEditorRoleId) {
      return;
    }

    const nextDraft = acceptAiEdits(activeEditorRoleId, undefined, {
      baseDraftFields: formDraft,
    });

    if (nextDraft) {
      setFormDraft(nextDraft);
    }
  }, [acceptAiEdits, activeEditorRoleId, formDraft]);
  const handleDiscardAll = useCallback(() => {
    if (!activeEditorRoleId) {
      return;
    }

    discardAiEdits(activeEditorRoleId);
  }, [activeEditorRoleId, discardAiEdits]);
  const closeEditor = useCallback(
    (roleId: string) => {
      resolveActiveCustomRoleEdit();
      if (
        !useCustomRoleUpdateQueueStore.getState().hasActiveCustomRoleQueueWork()
      ) {
        clearRoleEditState(roleId);
      }
    },
    [clearRoleEditState, resolveActiveCustomRoleEdit],
  );
  const cancelEditor = useCallback(
    (roleId: string) => {
      discardAiEdits(roleId);
      closeEditor(roleId);
    },
    [closeEditor, discardAiEdits],
  );
  const startCreating = useCallback(() => setIsCreating(true), []);

  return {
    isCreating,
    startCreating,
    startEditing: setEditingRoleId,
    activeCustomRoleUpdateId,
    formDraft,
    resolvedEditingRole,
    roleForForm,
    createRoleForForm,
    closeEditor,
    cancelEditor,
    clearRoleEditState,
    pendingEditHandlers: {
      onAcceptField: handleAcceptField,
      onDiscardField: handleDiscardField,
      onAcceptAll: handleAcceptAll,
      onDiscardAll: handleDiscardAll,
    },
  };
}
